import { captureHostInputs } from '../configure/host-inputs-facade';
import { capturePersistencePolicy } from '../configure/persistence-facade';
import { resetLogCohort, setLogCohort } from '../useSessionRecordingStore/lib/logCohort';
import { logger } from '../useSessionRecordingStore/lib/logger';

// The pipeline-mode coordinator. Capture owns concurrent-call coalescing, the
// fallback to legacy on any failure, one pin log, and the pinned mode itself.
//
// Unlike the sync flag getters, this resolves once: the first resolution pins
// `legacy` or `ledger` for the rest of the page load and survives later flag
// changes. This module is the one pin singleton; only the testing door clears it.

export type PipelineMode = 'legacy' | 'ledger';

let pinned: PipelineMode | null = null;
let resolving: Promise<PipelineMode> | null = null;

/** Returns the per-page pinned persistence mode; pre-resolution reads use legacy. */
export const getPipelineMode = (): PipelineMode => pinned ?? 'legacy';

export const resolvePipelineMode = (): Promise<PipelineMode> => {
  if (pinned) return Promise.resolve(pinned);
  // Web-only rollout: the native host captures in Rust and never exercises the
  // ledger writer, so pin legacy without consulting the flag or logging it.
  // Recovery is unaffected — `hydrate` still drains an existing ledger DB.
  if (captureHostInputs.isNativeCaptureHost()) {
    pinned = 'legacy';
    return Promise.resolve(pinned);
  }
  if (!resolving) {
    // Normalize synchronous resolver throws to the legacy fallback.
    resolving = Promise.resolve()
      .then(() => capturePersistencePolicy.resolveLedgerEnabled())
      .then((result) =>
        typeof result === 'boolean' ? { enabled: result, reason: 'flag' } : result
      )
      .catch(() => ({ enabled: false, reason: 'resolver-error' }))
      .then(({ enabled, reason }) => {
        const mode: PipelineMode = enabled ? 'ledger' : 'legacy';
        pinned = mode;
        // Before the pin log, so LEDGER_MODE_PINNED carries the cohort too.
        setLogCohort(mode);
        logger.info({
          event_type: 'LEDGER_MODE_PINNED',
          details: { mode, reason },
        });
        return mode;
      })
      .catch((): PipelineMode => {
        // Preserve the module's never-reject contract even when an untyped
        // host returns malformed data or the telemetry sink itself throws.
        return pinned ?? 'legacy';
      });
  }
  return resolving;
};

/**
 * Not a public reset surface: called only by the testing door, so configuration,
 * the in-flight resolution and the pinned mode clear atomically.
 */
export const resetPipelineModeState = () => {
  pinned = null;
  resolving = null;
  resetLogCohort();
};
